import {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ResolverKind, ValueSource} from './valueSource.types';
import {createStaticValueSource} from './staticValueSources';
import {projectValueSource, personValueSource, clientValueSource, labelValueSource, roleValueSource} from './relayValueSources';
import {REMAINING_WORK_YES, REMAINING_WORK_NO} from '../remainingWorkCondition';
import {enumeratedCandidates, isSingleSelectEnumeration} from '../enumeratedValues';
import {ReportTaxonomyField} from '../../contracts/report-taxonomy.types';

const RELAY_REGISTRY: Partial<Record<ResolverKind, ValueSource>> = {
	project: projectValueSource,
	person: personValueSource,
	client: clientValueSource,
	label: labelValueSource,
	role: roleValueSource,
};

/**
 * Resolves the `ValueSource` for a given resolver kind. `'boolean'` and `'workflowStatus'` are
 * built here (not in the static registry) because their candidate labels are user-facing and
 * must go through `useIntl`; the source is memoized on the formatted strings so identity stays
 * stable across renders unless the active locale's translations change. The relay-backed kinds
 * are unchanged plain registry lookups.
 *
 * `'enumeratedValues'` is the one kind whose candidates are not fixed: they are the vocabulary the
 * taxonomy publishes on the field being edited, so the caller has to pass `field` alongside the
 * kind. Its labels are the catalog's own strings and are NOT run through `useIntl` - the same
 * treatment `field.label` already gets, and the only honest one, since translating a value the
 * picker has to write back verbatim would change what the condition stores.
 *
 * The workflow-status ids are status CATEGORIES, not status-column ids, because that is what the
 * status filter compares against server-side. Every project's own columns ("In Review", "QA",
 * "Blocked", ...) roll up into these three, so the list is complete and identical for every
 * company — a per-project status-name picker would be both unavailable company-wide and wrong on
 * the wire.
 */
export function useValueSource(kind: ResolverKind, field?: ReportTaxonomyField): ValueSource {
	const {formatMessage} = useIntl();

	const yes = formatMessage({id: 'common.yes', defaultMessage: 'Yes'});
	const no = formatMessage({id: 'common.no', defaultMessage: 'No'});
	const todo = formatMessage({id: 'ai_report_builder.conditions.status_todo', defaultMessage: 'To Do'});
	const inProgress = formatMessage({id: 'common.in_progress', defaultMessage: 'In Progress'});
	const done = formatMessage({id: 'common.done', defaultMessage: 'Done'});

	// singleSelect: Yes and No are two readings of one comparison, not two values that can both
	// hold. Without it, ticking both wrote `values: ['true','false']`, which `operatorForValues`
	// then promoted to IN - an operator none of the boolean filter fields (BLOCKED, BUG,
	// HIGH_PRIORITY) declares, so the report-data fetch failed validation outright.
	const booleanSource = useMemo(
		() => ({
			...createStaticValueSource([
				{id: 'true', label: yes},
				{id: 'false', label: no},
			]),
			singleSelect: true,
		}),
		[yes, no]
	);

	// `echoUnknownIds` because these three are the complete status vocabulary: anything else stored
	// on a status condition is a stale value (e.g. a status name written before the filter's value
	// vocabulary was settled). Showing it as-is keeps the summary honest — without it the condition
	// would collapse to "N selected" and the bad value would be invisible.
	const workflowStatusSource = useMemo(
		() =>
			createStaticValueSource(
				[
					{id: 'TODO', label: todo},
					{id: 'INPROGRESS', label: inProgress},
					{id: 'DONE', label: done},
				],
				{echoUnknownIds: true}
			),
		[todo, inProgress, done]
	);

	// singleSelect: the two candidates are mutually exclusive readings of one comparison ("has work
	// left" vs "has none"), not a set of values that can coexist, so picking one replaces the other
	// rather than adding to it.
	const remainingWorkSource = useMemo(
		() => ({
			...createStaticValueSource([
				{id: REMAINING_WORK_YES, label: yes},
				{id: REMAINING_WORK_NO, label: no},
			]),
			singleSelect: true,
		}),
		[yes, no]
	);

	// Memoised on the field so the source keeps a stable identity across renders; the taxonomy is
	// held in state and its field objects are not rebuilt, so this recomputes only when the taxonomy
	// itself is replaced. `echoUnknownIds` because the declared list is this field's COMPLETE
	// vocabulary: an id outside it can only be a stale or agent-authored value, and showing it is
	// what stops the tile collapsing to "N selected" and hiding it.
	const enumeratedSource = useMemo(() => {
		if (!field) {
			return createStaticValueSource([]);
		}
		return {
			...createStaticValueSource(enumeratedCandidates(field, formatMessage), {echoUnknownIds: true}),
			singleSelect: isSingleSelectEnumeration(field),
		};
	}, [field, formatMessage]);

	if (kind === 'enumeratedValues') {
		return enumeratedSource;
	}
	if (kind === 'boolean') {
		return booleanSource;
	}
	if (kind === 'workflowStatus') {
		return workflowStatusSource;
	}
	if (kind === 'remainingWork') {
		return remainingWorkSource;
	}
	// Falls back to an empty static source for any kind not in the relay registry, so a picker
	// never calls `.useCandidates`/`.resolveLabels` on `undefined` (e.g. an unrecognised resolver
	// kind arriving from the taxonomy).
	return RELAY_REGISTRY[kind] ?? createStaticValueSource([]);
}
